<template>
  <div class="request-element">
    <div class="request-header">
      <span class="request-type-badge">
        <i class="fas fa-exchange-alt"></i>
        <span>Requête API</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="request-content">
     
      <div class="form-group">
        <label>Requête</label>
        <div class="request-input-group">
          <select 
            v-model="localElement.method" 
            class="method-select" 
            :class="methodClass"
            @change="saveChanges('method')"
          >
          
            <option value="POST">POST</option>
          </select>
          <input 
            type="text" 
            v-model="localElement.url" 
            class="url-input" 
            placeholder="https://api.example.com"
            @blur="saveChanges('url')"
            @keydown.enter="$event.target.blur()"
          />
        </div>
      </div>
      
      <div class="form-group">
        <label>Variables</label>
         <vue-multiselect
          v-model="selectedVariables"
          :options="variables"
          :multiple="true"
          track-by="id"
          label="slug"
          placeholder="Sélectionner les variables"
          @input="onVariablesChange"
          @open="() => console.log('📂 RequestElement - Multiselect ouvert')"
          @close="() => console.log('📁 RequestElement - Multiselect fermé')"
          @select="(option) => {   onVariablesChange(selectedVariables); }"
          @remove="(option) => {   onVariablesChange(selectedVariables); }"
        >
          <template #tag="{ option }">
            <span class="multiselect-tag">{{ option.slug }}</span>
          </template>
          <template #option="{ option }">
            <div class="multiselect-option-debug">
              <strong>{{ option.slug }}</strong> (ID: {{ option.id }})
            </div>
          </template>
        </vue-multiselect>
      </div>
      
      <!-- Indicateur de sauvegarde -->
      <div v-if="isSaving" class="saving-indicator">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Sauvegarde en cours...</span>
      </div>
      
      <div v-if="lastSaved" class="last-saved-indicator">
        <i class="fas fa-check-circle"></i>
        <span>Dernière sauvegarde : {{ formatLastSaved }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import { axiosPeelo } from '@/utils/axios'

export default {
  name: 'RequestElement',
  components: {
    VueMultiselect
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    idBot: {
      type: String,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {

    return {
      localElement: {
        method: this.element.method || 'GET',
        url: this.element.url || '',
        datas: this.element.datas || []
      },
      selectedVariables: [],
      isSaving: false,
      lastSaved: null
    }
  },
  async created() {
     
    this.initializeSelectedVariables()
  },
  watch: {
    variables: {
      immediate: true,
      handler(newVariables, oldVariables) {
        this.initializeSelectedVariables()
      }
    },
    element: {
      deep: true,
      handler(newElement, oldElement) {
       
        this.localElement = {
          method: newElement.method || 'GET',
          url: newElement.url || '',
          datas: newElement.datas || []
        };
         this.initializeSelectedVariables();
      }
    }
  },
  computed: {
    methodClass() {
      const method = (this.element.method || 'GET').toUpperCase();
      switch (method) {
        case 'GET':
          return 'method-get';
        case 'POST':
          return 'method-post';
        case 'PUT':
          return 'method-put';
        case 'DELETE':
          return 'method-delete';
        default:
          return 'method-other';
      }
    },
    formatLastSaved() {
      if (this.lastSaved) {
        const date = new Date(this.lastSaved);
        return date.toLocaleString();
      }
      return 'Aucune sauvegarde effectuée';
    },
    getNonVariableDatas() {
      const availableVariableIds = new Set(this.variables.map(v => v.id));
      return this.localElement.datas.filter(dataId => !availableVariableIds.has(dataId));
    },
    getVariableDatas() {
      const availableVariableIds = new Set(this.variables.map(v => v.id));
      return this.localElement.datas.filter(dataId => availableVariableIds.has(dataId));
    }
  },
  methods: {
    initializeSelectedVariables() {
      
      
      // Initialiser datas comme array si c'est undefined
      if (!this.localElement.datas) {

        this.localElement.datas = [];
      }
      
      // Synchronise selectedVariables avec les ids présents dans element.datas
      if (this.variables && this.variables.length > 0 && Array.isArray(this.localElement.datas)) {

        
        // Trouve les variables qui sont déjà dans datas
        this.selectedVariables = this.variables.filter(v => {
          const isSelected = this.localElement.datas.includes(v.id);
         
          return isSelected;
        });
        
       } else {
        this.selectedVariables = [];
       }
      
     
    },
    
    saveChanges(field) {
       
      this.isSaving = true;
      this.lastSaved = new Date();
      
      // Nettoyer l'objet pour ne garder que datas, method, url, etc.
      const { datas, method, url } = this.localElement;
      const updatedElement = { ...this.element, datas, method, url };
      
      
      // Émettre l'événement update vers le parent pour mettre à jour le flow
      this.$emit('update', { ...updatedElement });
      
      // Simuler une sauvegarde (remplacer par vraie API si nécessaire)
      setTimeout(() => {
         this.isSaving = false;
      }, 300);
    },
    
    onVariablesChange(selected) {
        
      // Mettre à jour selectedVariables (pour l'affichage)
      this.selectedVariables = selected || [];
      
      // SIMPLE : Mettre à jour directement les datas avec les IDs sélectionnés
      // On garde "last_input" et on ajoute les IDs de variables
      const variableIds = this.selectedVariables.map(v => v.id);
      
      // Garder seulement "last_input" s'il existe, et ajouter les variables
      const hasLastInput = this.element.datas && this.element.datas.includes('last_input');
      this.localElement.datas = hasLastInput ? ['last_input', ...variableIds] : [...variableIds];
      
       
      // Sauvegarder comme les autres champs
      this.saveChanges('datas');
    }
  }
}
</script>

<style scoped>
@import 'vue-multiselect/dist/vue-multiselect.css';

/* Reset du style par défaut de vue-multiselect */
.vue-multiselect {
  min-height: 40px;
}

/* Container principal */
.multiselect__tags {
  min-height: 40px;
  padding: 6px 40px 0 8px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

/* Liste déroulante */
.multiselect__content-wrapper {
  position: fixed !important;
  width: auto !important;
  min-width: 100%;
  margin-top: 4px;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 9999;
}

/* Options dans la liste */
.multiselect__option {
  padding: 8px 12px;
}

/* Option sélectionnée */
.multiselect__option--selected {
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 600;
}

/* Option au survol */
.multiselect__option--highlight {
  background: #3b82f6;
  color: #fff;
}

/* Tags sélectionnés */
.multiselect__tag {
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 6px;
  padding: 4px 26px 4px 8px;
  margin: 0 4px 4px 0;
}

/* Bouton de suppression du tag */
.multiselect__tag-icon {
  background: #bfdbfe;
  border-radius: 4px;
}

.multiselect__tag-icon:after {
  color: #1d4ed8;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: #93c5fd;
}

/* Input de recherche */
.multiselect__input {
  background: transparent;
  margin-bottom: 4px;
  padding: 0;
  font-size: 0.9rem;
}

.request-element {
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.request-element:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid #cbd5e1;
}

.request-type-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #475569;
}

.request-type-badge i {
  color: #64748b;
}

.element-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  background: white;
  border: none;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  width: 28px;
  height: 28px;
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.action-btn.delete-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.request-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.method-select {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  width: 120px;
  border: 1px solid #cbd5e1;
  cursor: pointer;
}

.request-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.url-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #0f172a;
}

.multiselect-tag {
  background: #f1f5f9;
  color: #334155;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin: 2px;
  display: inline-block;
}

.method-get {
  background: #dbeafe;
  color: #2563eb;
}

.method-post {
  background: #dcfce7;
  color: #16a34a;
}

.method-put {
  background: #fef3c7;
  color: #d97706;
}

.method-delete {
  background: #fee2e2;
  color: #dc2626;
}

.method-other {
  background: #f3f4f6;
  color: #4b5563;
}

.request-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.request-url {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.url-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.85rem;
}

.url-value {
  color: #0f172a;
  font-family: monospace;
  background: #f8fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.request-data {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.data-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.85rem;
}

.data-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.data-tag {
  background: #f1f5f9;
  color: #334155;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  border: 1px solid #e2e8f0;
}

.edit-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.edit-button:hover {
  background: #f8fafc;
  color: #334155;
  border-color: #94a3b8;
}

/* Styles pour la section debug */
.debug-section {
  background: linear-gradient(145deg, rgba(243, 244, 246, 0.8), rgba(229, 231, 235, 0.6));
  border: 2px dashed rgba(156, 163, 175, 0.4);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.debug-section h4 {
  margin: 0 0 12px 0;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
}

.debug-info {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
}

.debug-info p {
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  color: #4b5563;
}

.variables-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 120px;
  overflow-y: auto;
}

.variable-item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  font-size: 0.75rem;
}

.variable-id {
  background: linear-gradient(145deg, rgba(37, 211, 102, 0.15), rgba(37, 211, 102, 0.1));
  color: #059669;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 500;
  border: 1px solid rgba(37, 211, 102, 0.2);
  min-width: 60px;
  text-align: center;
}

.variable-type {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.1));
  color: #2563eb;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 500;
  border: 1px solid rgba(59, 130, 246, 0.2);
  min-width: 50px;
  text-align: center;
  font-size: 0.7rem;
}

.variable-slug {
  color: #6b7280;
  font-family: monospace;
  background: rgba(243, 244, 246, 0.6);
  padding: 2px 6px;
  border-radius: 4px;
  flex: 1;
}

.selected-variables {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.selected-variable {
  background: linear-gradient(145deg, rgba(168, 85, 247, 0.15), rgba(168, 85, 247, 0.1));
  color: #7c3aed;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(168, 85, 247, 0.2);
}

.no-variables {
  color: #9ca3af;
  font-style: italic;
  font-size: 0.8rem;
}

.saving-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  color: #2563eb;
  font-size: 0.85rem;
  animation: pulse 1.5s infinite;
}

.saving-indicator i {
  color: #2563eb;
  animation: spin 1s linear infinite;
}

.last-saved-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(145deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05));
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 6px;
  color: #059669;
  font-size: 0.85rem;
  transition: all 0.3s ease;
}

.last-saved-indicator i {
  color: #10b981;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

.datas-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.datas-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.85rem;
}

.non-variable-datas {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.non-variable-data {
  background: linear-gradient(145deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.1));
  color: #d97706;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(245, 158, 11, 0.2);
  font-family: monospace;
}

.variable-datas {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.variable-data {
  background: linear-gradient(145deg, rgba(37, 211, 102, 0.15), rgba(37, 211, 102, 0.1));
  color: #059669;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(37, 211, 102, 0.2);
  font-family: monospace;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
  font-size: 0.8rem;
}

.no-datas {
  color: #9ca3af;
  font-style: italic;
  font-size: 0.8rem;
}

.debug-multiselect {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  color: #2563eb;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 4px 0;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.multiselect-option-debug {
  padding: 4px 0;
}

.multiselect-option-debug strong {
  color: #374151;
}
</style>
